import {
  MessageSquare,
  ShieldCheck,
  Sparkles,
  Mail,
  Brain,
  LifeBuoy,
  Crown,
} from 'lucide-react';
import ScrollReveal from './ScrollReveal';

const pillars = [
  {
    icon: Sparkles,
    title: 'Expert-grade SEO answers, instantly',
    description:
      'Ask anything about your site, audit findings, competitors, keywords, or technical SEO and get clear, actionable answers in seconds \u2014 no waiting on email queues or office hours.',
    accent: 'bg-cv-teal',
  },
  {
    icon: Brain,
    title: 'Already knows your reports & history',
    description:
      'Every chat is pre-loaded with your latest audit, CV Score, KIE snapshot, and prior recommendations. Skip the "let me explain my site" preamble \u2014 the agent already has the context.',
    accent: 'bg-cv-navy',
  },
  {
    icon: MessageSquare,
    title: 'Multi-turn chat with full memory',
    description:
      'Ask follow-ups, change direction, refine the brief \u2014 the agent remembers the entire thread, cites sources, and stays scoped to your SEO so it never drifts into guesswork.',
    accent: 'bg-cv-navy',
  },
  {
    icon: LifeBuoy,
    title: 'One-click human escalation',
    description:
      'When you want a human in the loop, a single click hands the conversation \u2014 with full context, history, and citations \u2014 to a real engineer. No re-explaining, no lost time.',
    accent: 'bg-cv-orange',
  },
];

const ExpertChatHighlight = () => (
  <section
    id="expert-chat"
    className="py-16 md:py-20"
    aria-label="Your AI SEO expert"
  >
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <ScrollReveal>
        <div className="relative overflow-hidden rounded-3xl border border-cv-teal/70 bg-cv-teal/10 p-6 sm:p-10 shadow-md">
          {/* Decorative orbs */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cv-teal/60 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-cv-sky/50 blur-3xl"
          />

          {/* Premium Access badge — top-right corner of the card */}
          <div
            className="absolute top-4 right-4 sm:top-5 sm:right-5 z-10"
            aria-label="Premium access only"
          >
            <span className="group relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white shadow-lg bg-cv-gold ring-1 ring-white/40">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full bg-cv-gold/50 blur-md -z-10"
              />
              <Crown className="w-3.5 h-3.5 drop-shadow-sm" />
              Premium Access Only
            </span>
          </div>

          <div className="relative">
            {/* Header */}
            <div className="max-w-3xl mb-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 border border-cv-teal/40 text-cv-navy text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                Your AI SEO Expert
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-cv-navy leading-tight">
                Talk to an{' '}
                <span className="text-cv-navy">
                  AI SEO agent that already knows your site
                </span>
              </h2>
              <p className="text-cv-navy/80 mt-3 text-base sm:text-lg leading-relaxed">
                Crawler Vision ships with an always-on AI expert pre-loaded with your reports, audit
                findings, scores, and recommendations. Ask follow-up questions, get unstuck on
                technical SEO, plan next steps &mdash; and when you need a human, escalate to a real
                engineer in one click without losing a single line of context.
              </p>
            </div>

            {/* Capability cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {pillars.map((p) => (
                <div
                  key={p.title}
                  className="rounded-2xl border border-white/80 bg-white/90 backdrop-blur-sm p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`shrink-0 w-11 h-11 rounded-xl ${p.accent} flex items-center justify-center cv-emboss`}
                    >
                      <p.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-cv-navy mb-1">{p.title}</h3>
                      <p className="text-sm text-cv-navy/70 leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust footnote */}
            <p className="mt-6 text-xs text-cv-navy/60 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Scope-guarded answers with citations. Email any conversation as an executive summary or
              full transcript &mdash; great for audit trails and async handoffs.
              <Mail className="w-3.5 h-3.5 ml-0.5" />
            </p>
          </div>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

export default ExpertChatHighlight;
